import store from "@/store";
import { i18nInstance, loadLanguageAsync } from "@/i18n";

import {
    FLOW_STEP_PAYMENT,
    FLOW_STEP_CONFIRM_ORDER,
    FLOW_STEP_ORDER_CONFIRMED
} from "@/store/modules/flow";
import { createRouter, createWebHistory } from "vue-router";

const PageNotFound = () =>
    import(/* webpackChunkName: "page-not-found" */ "@/pages/PageNotFound.vue");
const PagePayment = () =>
    import(
        /* webpackMode: "lazy", webpackChunkName: "page-order-details" */ "@/pages/PagePayment.vue"
    );
const PageConfirmOrder = () =>
    import(
        /* webpackMode: "lazy", webpackChunkName: "page-order-confirmed" */ "@/pages/PageConfirmOrder"
    );
const PageOrderConfirmed = () =>
    import(
        /* webpackMode: "lazy", webpackChunkName: "page-orderconfirmed" */ "@/pages/PageOrderConfirmed"
    );


const paths = require("./paths");

const defaultRoutes = [
    {
        path: paths.payment,
        name: "page_payment",
        component: PagePayment,
        meta: {
            flowStep: FLOW_STEP_PAYMENT
        }
    },
    {
        path: paths.confirmOrder,
        name: "page_confirm_order",
        component: PageConfirmOrder,
        meta: {
            flowStep: FLOW_STEP_CONFIRM_ORDER
        }
    },
    {
        path: paths.orderConfirmed,
        name: "page_order_confirmed",
        component: PageOrderConfirmed,
        meta: {
            flowStep: FLOW_STEP_ORDER_CONFIRMED,
            allowReload: true
        }
    },    
    {
        path: '/:catchAll(.*)',
        component: PageNotFound,
        name: 'catch-all'
    },
];

// eslint-disable-next-line import/no-dynamic-require
const appRoutes = require(`@/apps/${process.env.VUE_APP_NAME}/routes`).default;

const routes = [...defaultRoutes, ...appRoutes];

const flowToRoute = function(type, step = null) {
    if (step) {
        return routes.filter(
            r =>
                r.meta &&
                r.meta.flowStep === step &&
                (!r.meta.flowType || r.meta.flowType === type)
        )[0];
    }

    return routes.filter(
        r => r.meta && r.meta.flowType === type && !r.meta.flowStep
    )[0];
};

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
});

router.flowToRoute = flowToRoute;

async function reset(next, replace = true) {
    await store.dispatch("flow/resetFlow");
    if (replace) {
        next({ path: "/", replace: true });
    } else {
        next();
    }
}

// pulled from index.html
const defaultTitle = document.title;

router.beforeEach((to, from, next) => {
    const title = [defaultTitle];
    const key = `${to.name}.title`;

    try {
        if (i18nExp.global.te(key)) {
            const pageTitle = i18nExp.global.t(key);
            if (pageTitle) title.unshift(pageTitle);
        }
    } catch (e) {
        // do nothing
    }
    next();
});

router.beforeEach((to, _from, next) => {
    // support setting language via lang query params
    if (to.query.lang) {
        const availableLanguages = JSON.parse(
            process.env.VUE_APP_AVAILABLE_LANGUAGES
        );
        if (availableLanguages.includes(to.query.lang)) {

            i18nInstance().global.locale.value = to.query.lang
        }
    }

    if (to.path === "/payment/" && to.query.reference) {
        // eslint-disable-next-line no-undef
        const state = $cookies.get(`order-${to.query.reference}`);
        if (!state) return reset(next);
        i18nInstance().global.locale.value = state.locale
        
        store.commit("order/reset", state.productType);
        store.commit("order/setOrderId", state.orderId);
        store.commit("order/setPriceOre", state.priceOre);
        store.commit("order/setDetails", state.details);
        store.commit("order/setDeliveryMethod", state.deliveryMethod);
        store.commit("flow/continueFlow", {
            type: state.flowType,
            step: state.flowStep
        });

        let provider = to.query.provider;
        store.commit("order/setPaymentProvider", provider);


        switch (provider) {
            case "Netaxept":
                if (to.query.responseCode === "Cancel") return reset(next);
                store.commit("order/setTransactionId", to.query.transactionId);
                break;
            case "ApplePay":
                store.commit("order/setTransactionId", to.query.transactionId);
                break;
            case "Developer":
                if (process.env.VUE_APP_SERVER === "live") {
                    throw new Error("Developer provider cannot be used on live environment");
                }
                break;
        }

        store
            .dispatch("flow/gotoNextStep")
            .then(step => {
                const match = flowToRoute(state.flowType, step);
                if (!match) {
                    throw new Error("no match");
                }
                next({
                    path: match.path,
                    replace: !match.meta.allowReload
                });
            })
            .catch(() => {
                next({
                    path: "/",
                    replace: true
                });
            });

        return;
    }

    // this guard is for ensuring that the matched route matches the flow
    // this will reset the flow, if you go back end forward in a flow, where it
    // is not allowed
    const [flowType, flowStep] = store.getters["flow/getFullStep"];
    if (to.matched.length === 1) {
        if (flowType && flowStep) {
            // check if current step matches flow
            const expect = flowToRoute(flowType, flowStep);
            if (expect && expect.name !== to.name) {
                // if the dest allow reloads, we expect it to handle init flow correctly
                // and only resets the current flow without redirects
                reset(next, !to.meta.allowReload);
                return;
            }
        }

        if (to.meta.flowStep && !to.meta.allowReload) {
            // if no current step, it is a reload or direct access
            if (!flowStep) {
                reset(next);
                return;
            }
        }
    }

    next();
});

export default router;
